import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NoteIcon from "@mui/icons-material/Note";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { pick } from "lodash";
import { useMemo, useState } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  CertificateTemplateRequest,
  ICertificateTemplate,
} from "services/KnowfullyAdminClient";
import { CommonFormProps, Orientation } from "types/common-types";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const useStyles = makeStyles(() => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<CertificateTemplateRequest>();

const CertificateTemplateForm = ({
  entity: certificateTemplate,
  submitLabel,
  onCancel,
  onSubmit,
}: CommonFormProps<ICertificateTemplate, CertificateTemplateRequest> &
  FormProps<CertificateTemplateRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const [orientation, setOrientation] = useState(
    certificateTemplate?.isLandscape
      ? Orientation.Landscape
      : Orientation.Portrait
  );

  const init = useMemo(
    () => ({
      orientation: certificateTemplate?.isLandscape
        ? Orientation.Landscape
        : Orientation.Portrait,
      ...pick(certificateTemplate ?? {}, [
        "name",
        "description",
        "value",
        "templateHtml",
        "unaccredited",
        "isLandscape",
        "organizationId",
      ]),
    }),
    [certificateTemplate]
  );

  return useMemo(() => {
    const innerOnSubmit = (values: CertificateTemplateRequest) => {
      values.isLandscape = orientation === Orientation.Landscape;
      return onSubmit(values);
    };

    return (
      <Form
        onSubmit={innerOnSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values, form }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={form.change}
                  mode={
                    certificateTemplate
                      ? ContextOrganizationFieldMode.EditManuallyTriggered
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "certificateTemplateFormNameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="nameText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "certificateTemplateFormValueLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="valueText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        multiline={true}
                        rows={4}
                        fullWidth
                        label={t({
                          id: "certificateTemplateFormDescriptionLabel",
                        })}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="descriptionText"
                      />
                    </Grid>
                  )}
                </Field>
                <Field name="unaccredited">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({
                          id: "certificateTemplateFormUnaccreditedLabel",
                        })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="unaccreditedText"
                      />
                    </Grid>
                  )}
                </Field>
                <Field name="orientation">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <h3 style={{ fontWeight: "normal", color: "grey" }}>
                        {t({
                          id: "orientation",
                        })}
                      </h3>
                      <ToggleButtonGroup
                        exclusive
                        onChange={(e, value) => setOrientation(value)}
                        size="large"
                      >
                        <Tooltip
                          title={t({
                            id: "landscape",
                          })}
                        >
                          <ToggleButton
                            value={Orientation.Landscape}
                            selected={orientation === Orientation.Landscape}
                            data-cy="landscapeButton"
                          >
                            <NoteIcon />
                          </ToggleButton>
                        </Tooltip>
                        <Tooltip
                          title={t({
                            id: "portrait",
                          })}
                        >
                          <ToggleButton
                            value={Orientation.Portrait}
                            selected={orientation === Orientation.Portrait}
                            data-cy="portraitButton"
                          >
                            <InsertDriveFileIcon />
                          </ToggleButton>
                        </Tooltip>
                      </ToggleButtonGroup>
                    </Grid>
                  )}
                </Field>
                <Box mb={3}></Box>

                <Field name="templateHtml">
                  {({ input, meta }) => (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                      <FieldRichText
                        input={input}
                        meta={meta}
                        label={t({
                          id: "certificateTemplateFormContentLabel",
                        })}
                        init={{
                          height: 470,
                          paste_data_images: true,
                        }}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="saveButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    init,
    orientation,
    onSubmit,
    certificateTemplate,
    styles.feedback,
    onCancel,
    t,
    submitLabel,
  ]);
};

export default CertificateTemplateForm;
